import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useTreeDndUiStore } from '@/components/tree-dnd/tree-dnd-ui-store';
import { useConditionEditSheetConfig } from '@/components/tree-dnd/tree-dnd-conditions/condition-edit-sheet-context';
import { useTreeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import { getNodeAtPath } from '@/components/tree-dnd/tree-dnd-utils';
import { useI18nContext } from '@/i18n/i18n-react';
import { cn } from '@/lib/utils';

function isPortaledOverlayTarget(target: EventTarget | null) {
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest('[data-slot="popover-content"]') ||
      target.closest('[role="listbox"]') ||
      target.closest('[data-radix-popper-content-wrapper]')
  );
}

interface ConditionEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  children: ReactNode;
  footerContent?: ReactNode;
  contentClassName?: string;
}

export function ConditionEditSheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footerContent,
  contentClassName,
}: ConditionEditSheetProps) {
  const { LL } = useI18nContext();
  const sheetConfig = useConditionEditSheetConfig();
  const requestAddSheet = useTreeDndUiStore((state) => state.requestAddSheet);
  const tree = useTreeDndStore((state) => state.tree);
  const setNegated = useTreeDndStore((state) => state.setNegated);

  const showAddAnother = sheetConfig?.showAddAnother ?? false;
  const showMoreOptions = sheetConfig?.showMoreOptions ?? true;
  const parentPath = sheetConfig?.parentPath ?? [];
  const path = sheetConfig?.path;
  const node = path ? getNodeAtPath(tree, path) : null;
  const negated = node?.negated ?? false;

  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setMoreOptionsOpen(negated);
    }
    if (!open) {
      setMoreOptionsOpen(false);
    }
    wasOpenRef.current = open;
  }, [open, negated]);

  const handleAddAnother = () => {
    onOpenChange(false);
    requestAddSheet(parentPath);
  };

  const handleNegatedChange = (checked: boolean) => {
    if (!path) return;
    setNegated(path, checked);
    if (checked) setMoreOptionsOpen(true);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full max-w-[min(100vw,26rem)] flex-col gap-0 p-0 sm:max-w-[26rem]',
          contentClassName
        )}
        onPointerDownOutside={(event) => {
          if (isPortaledOverlayTarget(event.target)) {
            event.preventDefault();
          }
        }}
        onFocusOutside={(event) => {
          if (isPortaledOverlayTarget(event.target)) {
            event.preventDefault();
          }
        }}
        onInteractOutside={(event) => {
          if (isPortaledOverlayTarget(event.target)) {
            event.preventDefault();
          }
        }}
      >
        <SheetHeader className="space-y-3 border-b px-6 py-4 text-left">
          <div className="space-y-1.5 pr-8">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </div>

          {path && showMoreOptions ? (
            <Collapsible
              open={moreOptionsOpen}
              onOpenChange={setMoreOptionsOpen}
            >
              <div className="flex justify-center">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    <span>
                      {moreOptionsOpen
                        ? LL.audiences.filters.editSheet.lessOptions()
                        : LL.audiences.filters.editSheet.moreOptions()}
                    </span>
                    {moreOptionsOpen ? (
                      <ChevronUp className="size-3.5 shrink-0" />
                    ) : (
                      <ChevronDown className="size-3.5 shrink-0" />
                    )}
                  </button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent>
                <div className="flex flex-col gap-1 px-2 pb-2.5 pt-0.5">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="condition-negate-filter"
                      size="compact"
                      checked={negated}
                      onCheckedChange={handleNegatedChange}
                      className="shrink-0"
                    />
                    <Label
                      htmlFor="condition-negate-filter"
                      className="text-sm font-normal text-foreground cursor-pointer"
                    >
                      {LL.audiences.filters.editSheet.negateFilter()}
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {LL.audiences.filters.editSheet.negateFilterHelp()}
                  </p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          ) : null}
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-6">
          {children}
        </div>
        <div className="flex flex-col gap-3 border-t px-6 py-4">
          {footerContent}
          <div className="flex flex-col gap-2">
            <Button type="button" onClick={() => onOpenChange(false)}>
              {LL.audiences.filters.editSheet.ok()}
            </Button>
            {showAddAnother ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleAddAnother}
              >
                {LL.audiences.filters.editSheet.addAnotherCondition()}
              </Button>
            ) : null}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
